import { useCallback, useMemo, useState } from 'react'

/// Lightweight multi-select state for list views (vendors, parts,
/// checklists, work orders). Tracks a Set of ids and exposes the
/// common helpers so each page doesn't reinvent toggle / select-all /
/// clear / isSelected. Designed for soft-delete cleanup flows — pair
/// with the matching `api.<module>.bulkRemove` endpoint.
export function useBulkSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  // Replace the entire selection (used for "select all visible" and to
  // drop ids that disappeared from the list after a delete).
  const replaceWith = useCallback((ids: string[]) => {
    setSelected(new Set(ids))
  }, [])

  const isSelected = useCallback((id: string) => selected.has(id), [selected])

  const api = useMemo(
    () => ({
      ids: Array.from(selected),
      count: selected.size,
      isSelected,
      toggle,
      clear,
      replaceWith,
      // Returns true when every id in `visible` is currently selected.
      // Used to drive the header "select-all" tristate.
      allSelected: (visible: string[]) =>
        visible.length > 0 && visible.every((id) => selected.has(id)),
      // Returns true when SOME but not all visible ids are selected —
      // for the indeterminate state of the header checkbox.
      someSelected: (visible: string[]) =>
        visible.some((id) => selected.has(id)) &&
        !visible.every((id) => selected.has(id)),
    }),
    [selected, isSelected, toggle, clear, replaceWith],
  )

  return api
}
